import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Link2Off, Package as PackageIcon, Plus, RefreshCcw, Save, Search } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import { useCapabilities } from '../../services/capability.service';
import {
  usePackageCapabilities,
  usePackages,
  useUpdatePackageCapabilities,
} from '../../services/package.service';
import {
  Capability,
  PackageCapabilityAssignment,
  PackageCapabilityAssignmentInput,
} from '../../types/admin';
import { EmptyState } from '../../components/admin/EmptyState';
import { formatAdminNumber } from '../../utils/numberFormat';
import { useGenerateVideoHealth } from '../../features/admin/generate-video/api';
import { ServicePresetCard } from '../../features/admin/generate-video/components/ServicePresetCard';
import { VideoHealthAlerts } from '../../features/admin/generate-video/components/VideoHealthAlerts';

interface PackageCapabilityDraftRow extends PackageCapabilityAssignmentInput {
  id: string;
  capabilityName: string;
  capabilityDescription?: string;
  capabilityIsActive?: boolean | null;
}

const toDraftRow = (assignment: PackageCapabilityAssignment): PackageCapabilityDraftRow => ({
  id: assignment.capabilityCode,
  capabilityCode: assignment.capabilityCode,
  capabilityName: assignment.capabilityName || assignment.capabilityCode,
  capabilityDescription: assignment.capabilityDescription || undefined,
  capabilityIsActive: assignment.capabilityIsActive,
  isEnabled: assignment.isEnabled ?? true,
});

const toNewDraftRow = (capability: Capability): PackageCapabilityDraftRow => ({
  id: capability.code,
  capabilityCode: capability.code,
  capabilityName: capability.name,
  capabilityDescription: capability.description,
  capabilityIsActive: capability.isActive,
  isEnabled: true,
});

const toAssignmentInput = (row: PackageCapabilityDraftRow): PackageCapabilityAssignmentInput => ({
  capabilityCode: row.capabilityCode,
  isEnabled: row.isEnabled ?? true,
});

export const PackageCapabilityManagement: React.FC = () => {
  const notification = useNotification();
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [draftRows, setDraftRows] = useState<PackageCapabilityDraftRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: packages = [], isLoading: isPackagesLoading, error: packagesError } = usePackages();
  const {
    data: packageCapabilities = [],
    isLoading: isPackageCapabilitiesLoading,
    isFetching: isPackageCapabilitiesFetching,
    error: packageCapabilitiesError,
  } = usePackageCapabilities(selectedPackageId);
  const {
    data: capabilityPage,
    isLoading: isCapabilitiesLoading,
    error: capabilitiesError,
  } = useCapabilities({ page: 1, limit: 100, status: 'all' });
  const updateMutation = useUpdatePackageCapabilities();
  const { data: videoHealth } = useGenerateVideoHealth();

  const capabilities = useMemo(() => capabilityPage?.data ?? [], [capabilityPage?.data]);
  const selectedPackage = packages.find((pkg) => pkg.publicId === selectedPackageId);
  const unmanagedAssignments = packageCapabilities.filter(
    (assignment) => !assignment.isCatalogCapability
  );
  const assignedCodeSet = useMemo(
    () => new Set(draftRows.map((row) => row.capabilityCode)),
    [draftRows]
  );
  const availableCapabilities = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return capabilities
      .filter((capability) => !assignedCodeSet.has(capability.code))
      .filter((capability) => {
        if (!normalizedSearch) return true;
        return (
          capability.name.toLowerCase().includes(normalizedSearch) ||
          capability.code.toLowerCase().includes(normalizedSearch)
        );
      });
  }, [assignedCodeSet, capabilities, searchTerm]);

  useEffect(() => {
    if (!selectedPackageId && packages.length > 0) {
      setSelectedPackageId(packages[0].publicId);
    }
  }, [packages, selectedPackageId]);

  useEffect(() => {
    setDraftRows(
      packageCapabilities
        .filter((assignment) => assignment.isCatalogCapability)
        .map((assignment) => toDraftRow(assignment))
    );
  }, [packageCapabilities]);

  const columns: GridColDef<PackageCapabilityDraftRow>[] = [
    {
      field: 'capabilityName',
      headerName: 'Capability',
      flex: 1.4,
      minWidth: 260,
      editable: false,
      renderCell: (params) => (
        <Box className="min-w-0">
          <Box className="flex items-center gap-2">
            <Typography variant="body2" className="font-semibold text-gray-900 dark:text-slate-100">
              {params.row.capabilityName}
            </Typography>
            {params.row.capabilityIsActive === false && (
              <Chip label="Inactive" size="small" color="warning" variant="outlined" />
            )}
          </Box>
          <Typography
            variant="caption"
            className="block truncate font-mono text-gray-500 dark:text-slate-400"
            title={params.row.capabilityCode}
          >
            {params.row.capabilityCode}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Enabled',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Switch
          size="small"
          checked={params.row.isEnabled ?? true}
          onChange={(_event, checked) => {
            setDraftRows((currentRows) =>
              currentRows.map((row) =>
                row.capabilityCode === params.row.capabilityCode
                  ? { ...row, isEnabled: checked }
                  : row
              )
            );
          }}
          color="primary"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 72,
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          color="error"
          size="small"
          onClick={() =>
            setDraftRows((currentRows) =>
              currentRows.filter((row) => row.capabilityCode !== params.row.capabilityCode)
            )
          }
          sx={{ minWidth: 36 }}
          title="Unassign capability"
        >
          <Link2Off size={16} />
        </Button>
      ),
    },
  ];

  const handleAssign = (capability: Capability) => {
    setDraftRows((currentRows) => {
      if (currentRows.some((row) => row.capabilityCode === capability.code)) {
        return currentRows;
      }
      return [...currentRows, toNewDraftRow(capability)];
    });
  };

  const handleSave = async () => {
    if (!selectedPackageId) return;

    try {
      const updatedAssignments = await updateMutation.mutateAsync({
        publicId: selectedPackageId,
        capabilities: draftRows.map((row) => toAssignmentInput(row)),
      });
      setDraftRows(
        updatedAssignments
          .filter((assignment) => assignment.isCatalogCapability)
          .map((assignment) => toDraftRow(assignment))
      );
      notification.success('Package capabilities updated successfully');
    } catch (error) {
      notification.error('Failed to update package capabilities');
    }
  };

  const isLoading = isPackagesLoading || isCapabilitiesLoading;
  const hasLoadError = packagesError || capabilitiesError || packageCapabilitiesError;

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      <Box className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Package Capabilities
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Assign runtime capabilities to subscription packages and toggle availability.
          </Typography>
        </Box>
        <Box className="flex items-center gap-2">
          <Button
            variant="outlined"
            startIcon={<RefreshCcw size={18} />}
            disabled={!selectedPackageId || isPackageCapabilitiesFetching}
            onClick={() =>
              setDraftRows(
                packageCapabilities
                  .filter((assignment) => assignment.isCatalogCapability)
                  .map((assignment) => toDraftRow(assignment))
              )
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={
              updateMutation.isPending ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <Save size={18} />
              )
            }
            disabled={!selectedPackageId || updateMutation.isPending}
            onClick={handleSave}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box className="flex h-[420px] items-center justify-center">
          <CircularProgress />
        </Box>
      ) : hasLoadError ? (
        <Alert severity="error">Failed to load package capability data.</Alert>
      ) : packages.length === 0 ? (
        <EmptyState
          Icon={PackageIcon}
          title="No packages available"
          description="Create a package before assigning capabilities."
        />
      ) : (
        <Box className="space-y-4">
          <Box className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
            <FormControl fullWidth>
              <InputLabel>Package</InputLabel>
              <Select
                label="Package"
                value={selectedPackageId}
                onChange={(event) => setSelectedPackageId(event.target.value)}
              >
                {packages.map((pkg) => (
                  <MenuItem key={pkg.publicId} value={pkg.publicId}>
                    {pkg.name} ({pkg.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedPackage && (
              <Box className="mt-3 flex flex-wrap gap-2">
                <Chip label={`${formatAdminNumber(selectedPackage.tokensPerMonth)} credits/cycle`} />
                {selectedPackage.advanceTokensPerMonth > 0 && (
                  <Chip
                    label={`${formatAdminNumber(
                      selectedPackage.advanceTokensPerMonth
                    )} advance credits/cycle`}
                  />
                )}
                <Chip label={`${formatAdminNumber(selectedPackage.maxAgents)} max agents`} />
                <Chip
                  label={selectedPackage.isActive ? 'Active' : 'Inactive'}
                  color={selectedPackage.isActive ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>

          {unmanagedAssignments.length > 0 && (
            <Alert severity="info">
              {unmanagedAssignments.length} non-catalog package capability
              {unmanagedAssignments.length > 1 ? 'ies are' : ' is'} preserved outside this editor:{' '}
              {unmanagedAssignments.map((assignment) => assignment.capabilityCode).join(', ')}
            </Alert>
          )}

          <VideoHealthAlerts
            health={videoHealth}
            capabilityCodes={draftRows
              .filter((row) => row.isEnabled)
              .map((row) => row.capabilityCode)}
          />

          <ServicePresetCard
            capabilities={capabilities}
            assignedCodes={assignedCodeSet}
            onAdd={(members) => members.forEach((capability) => handleAssign(capability))}
            onRemove={(codes) =>
              setDraftRows((currentRows) =>
                currentRows.filter((row) => !codes.includes(row.capabilityCode))
              )
            }
          />

          <Box className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Box className="rounded-lg bg-white shadow-sm dark:bg-slate-800">
              <Box className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700">
                <Box>
                  <Typography
                    variant="h6"
                    className="font-semibold text-gray-900 dark:text-slate-100"
                  >
                    Assigned Capabilities
                  </Typography>
                  <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                    Toggle capabilities enabled or disabled for this package.
                  </Typography>
                </Box>
                <Chip label={`${draftRows.length} assigned`} color="primary" variant="outlined" />
              </Box>

              {isPackageCapabilitiesLoading ? (
                <Box className="flex h-[360px] items-center justify-center">
                  <CircularProgress />
                </Box>
              ) : draftRows.length > 0 ? (
                <DataGrid
                  rows={draftRows}
                  columns={columns}
                  getRowId={(row) => row.capabilityCode}
                  disableRowSelectionOnClick
                  autoHeight
                  className="border-0"
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.default',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                />
              ) : (
                <Box className="p-10">
                  <EmptyState
                    Icon={PackageIcon}
                    title="No assigned capabilities"
                    description="Assign capabilities from the catalog list."
                  />
                </Box>
              )}
            </Box>

            <Box className="rounded-lg bg-white shadow-sm dark:bg-slate-800">
              <Box className="border-b border-gray-200 p-4 dark:border-slate-700">
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-900 dark:text-slate-100"
                >
                  Capability Catalog
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search capabilities"
                  className="mt-3"
                  InputProps={{
                    startAdornment: <Search size={16} className="mr-2 text-gray-400" />,
                  }}
                />
              </Box>
              <Box className="max-h-[640px] overflow-y-auto p-2">
                {availableCapabilities.length > 0 ? (
                  availableCapabilities.map((capability) => (
                    <Box
                      key={capability.publicId}
                      className="flex items-start justify-between gap-3 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    >
                      <Box className="min-w-0">
                        <Box className="flex items-center gap-2">
                          <Typography
                            variant="body2"
                            className="font-medium text-gray-900 dark:text-slate-100"
                          >
                            {capability.name}
                          </Typography>
                          {!capability.isActive && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          className="block truncate font-mono text-gray-500 dark:text-slate-400"
                          title={capability.code}
                        >
                          {capability.code}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Plus size={16} />}
                        onClick={() => handleAssign(capability)}
                        sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
                      >
                        Assign
                      </Button>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" className="px-3 py-6 text-center text-gray-500">
                    No available capabilities.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
